/**
 * The FACE issuance schedule.
 *
 * One verified unique human, one mint, ever. The allocation halves every
 * week after genesis, so the whole supply curve is settled inside a year
 * and the people who show up first are paid for the risk of showing up
 * first.
 *
 *   week 0 : 65 536 FACE      week  8 :   256 FACE
 *   week 1 : 32 768 FACE      week 16 :     1 FACE
 *   week 2 : 16 384 FACE      week 24 :     0.003 906 25 FACE
 *   week 3 :  8 192 FACE      week 42 :     0.000 000 01 FACE
 *
 * 65 536 is 2^16, so every halving is exact arithmetic on the grain
 * amount until the last one - no rounding drift, no dust accumulating in
 * the schedule. After week 42 the reward is below one grain and issuance
 * stops for good.
 *
 * There is no tail emission, no premine, no treasury and no founder
 * allocation: every grain that will ever exist is paid to a face.
 */

import { GRAINS_PER_FACE, params, type ChainParams } from './params.ts';

/** Last halving epoch that still pays at least one grain. */
export const FINAL_MINT_EPOCH = 42;

/** Human-facing name for one halving period. */
export const EPOCH_NAME = 'week';

/** Which halving epoch a timestamp falls in. Negative before genesis. */
export function halvingEpoch(timestampMs: number, p: ChainParams = params()): number {
  return Math.floor((timestampMs - p.genesisTimeMs) / p.halvingPeriodMs);
}

export function epochStartMs(epoch: number, p: ChainParams = params()): number {
  return p.genesisTimeMs + epoch * p.halvingPeriodMs;
}

/** Grains minted for a face whose claim is stamped at `timestampMs`. */
export function mintRewardAt(timestampMs: number, p: ChainParams = params()): bigint {
  return mintRewardForEpoch(halvingEpoch(timestampMs, p), p);
}

export function mintRewardForEpoch(epoch: number, p: ChainParams = params()): bigint {
  if (!Number.isFinite(epoch) || epoch < 0) return 0n;
  if (epoch > FINAL_MINT_EPOCH) return 0n;
  return p.genesisMintGrains >> BigInt(epoch);
}

/** Milliseconds until the reward halves again; null once issuance has ended. */
export function msUntilNextHalving(now: number, p: ChainParams = params()): number | null {
  const epoch = halvingEpoch(now, p);
  if (epoch > FINAL_MINT_EPOCH) return null;
  return epochStartMs(epoch + 1, p) - now;
}

/** Theoretical ceiling if every epoch's reward were claimed by one face. */
export function issuanceSchedule(p: ChainParams = params()): { epoch: number; grains: bigint; face: string }[] {
  const rows = [];
  for (let e = 0; e <= FINAL_MINT_EPOCH; e++) {
    const grains = mintRewardForEpoch(e, p);
    rows.push({ epoch: e, grains, face: formatFace(grains) });
  }
  return rows;
}

/* ------------------------------ unlocking --------------------------- */

/**
 * How much of an airdrop an account has earned out of lock.
 *
 * A newly minted allocation arrives locked. Each distinct new human who
 * joins naming this account as their referrer releases one nth of it, so
 * `unlockInvites` invites release all of it. The division is exact
 * because the numerator is the full minted amount: five fifths is the
 * whole thing, with no remainder stranded.
 */
export function unlockedGrains(mintedGrains: bigint, invites: number, p: ChainParams = params()): bigint {
  if (mintedGrains <= 0n) return 0n;
  const needed = Math.max(1, p.unlockInvites);
  const credited = Math.max(0, Math.min(invites, needed));
  if (credited >= needed) return mintedGrains;
  return (mintedGrains * BigInt(credited)) / BigInt(needed);
}

export function lockedGrains(mintedGrains: bigint, invites: number, p: ChainParams = params()): bigint {
  return mintedGrains - unlockedGrains(mintedGrains, invites, p);
}

/** Invites still needed for a full unlock. */
export function invitesRemaining(invites: number, p: ChainParams = params()): number {
  return Math.max(0, Math.max(1, p.unlockInvites) - Math.max(0, invites));
}

/* ---------------------------- formatting ---------------------------- */

export function formatFace(grains: bigint, maxDecimals = 8): string {
  const neg = grains < 0n;
  const v = neg ? -grains : grains;
  const whole = v / GRAINS_PER_FACE;
  let frac = (v % GRAINS_PER_FACE).toString().padStart(8, '0');
  frac = frac.slice(0, Math.max(0, Math.min(8, maxDecimals))).replace(/0+$/, '');
  const wholeStr = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + wholeStr + (frac ? '.' + frac : '');
}

/** Parse a user-typed amount ("12.5", "1,000") into grains. Throws on junk. */
export function parseFace(input: string): bigint {
  const s = String(input).trim().replace(/[, _]/g, '');
  if (!/^\d*(\.\d*)?$/.test(s) || s === '' || s === '.') throw new Error('not a valid amount');
  const [w, f = ''] = s.split('.');
  if (f.length > 8) throw new Error('FACE has at most 8 decimal places');
  return BigInt(w || '0') * GRAINS_PER_FACE + BigInt((f + '00000000').slice(0, 8));
}
